import { TInventory } from "~/types";
import { baseService } from "../base";
import { FilterParams, PaginationParams, PaginationResponse } from "../type";
import { InventoryStatusCode } from "~/utils/constants";
import {
  CheckInventoryParams,
  CreateInventoryParams,
  GetInventoriesParams,
  InventoryMetrics,
  QueryInventory,
  UpdateInventoryParams,
} from "./inventory.types";

export const inventoryService = {
  getInventories: async (
    params?: QueryInventory & FilterParams & PaginationParams
  ) => {
    const { data } = await baseService.get<PaginationResponse<TInventory>>(
      "/inventories",
      {
        params: params ?? {
          page: 1,
          pageSize: 10,
        },
      }
    );
    return data;
  },
  getInventory: async (id: string) => {
    const { data } = await baseService.get<TInventory>(`/inventories/${id}`);
    return data;
  },
  createInventory: async (formData: FormData) => {
    const { data } = await baseService.post("/inventories", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },
  createInventoryJson: async (payload: CreateInventoryParams) => {
    const { data } = await baseService.post("/inventories", payload);
    return data;
  },
  updateInventory: async ({ id, ...payload }: UpdateInventoryParams) => {
    const { data } = await baseService.patch(`/inventories/${id}`, payload);
    return data;
  },
  updateInventoryFormData: async ({
    id,
    formData,
  }: {
    id: string;
    formData: FormData;
  }) => {
    const { data } = await baseService.patch(`/inventories/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },
  deleteInventory: async (id: string) => {
    const { data } = await baseService.delete(`/inventories/${id}`);
    return data;
  },
  checkInventory: async (params: CheckInventoryParams) => {
    const { data } = await baseService.post("/inventories/check", params);
    return data;
  },
  getInventoryHistory: async (inventoryId: string) => {
    const { data } = await baseService.get(
      `/inventories/${inventoryId}/history`
    );
    return data;
  },
  bulkCheckInventory: async (
    inventoryIds: string[],
    isChecked: boolean,
    serviceTicketId?: string
  ) => {
    const { data } = await baseService.post("/inventories/bulk-check", {
      inventoryIds,
      checked: isChecked,
      serviceTicketId,
    });
    return data;
  },
  getMyInventories: async (
    params?: QueryInventory & FilterParams & PaginationParams
  ) => {
    const { data } = await baseService.get<PaginationResponse<TInventory>>(
      "/inventories/my-inventories",
      { params }
    );
    return data;
  },
  getMyInventory: async (id: string) => {
    const { data } = await baseService.get<TInventory>(`/inventories/my-inventories/${id}`);
    return data;
  },
  importInventories: async <T = TInventory[]>(formData: FormData) => {
    const response = await baseService.post<T>(
      "/inventories/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  },
  getInventoryMetrics: async () => {
    const { data } = await baseService.get<InventoryMetrics>(
      "/inventories/metrics"
    );
    return data;
  },

  exportInventory: async (params?: GetInventoriesParams) => {
    const { data } = await baseService.post(`/inventories/export/csv`, params, {
      responseType: "blob",
    });

    return data;
  },
  bulkUpdateInventory: async (params: {
    inventoryIds?: string[];
    allMatching?: boolean;
    filters?: Record<string, any>;
    excludedIds?: string[];
    status?: InventoryStatusCode;
    locationId?: string;
    notes?: string;
    artistName?: string;
    length?: number;
    width?: number;
    height?: number;
    packingType?: string;
  }) => {
    const { data } = await baseService.post(`/inventories/bulk-update`, params);
    return data;
  },
  getInventoriesByIds: async (ids: string[]) => {
    if (!ids.length) return [];
    const { data } = await baseService.post<TInventory[]>("/inventories/by-ids", { ids });
    return data;
  },
};
